import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';

import { Col, Row, Table, Button, Form, notification, Tooltip, Spin } from "antd";
import { PlusOutlined, SettingFilled, AppstoreFilled } from '@ant-design/icons';

import { getControls, getControlsByCode, createControl, updateControl, deleteControl } from "../../API/controls";
import { getControlsArea, getCategories, getPriorities, getFrameworks, getTypes, getRisks } from "../../API/controls";

import ControlsModal from "./components/ControlsModal/ControlsModal";
import ControlSearch from "./components/ControlSearch/ControlSearch";
import Columns from './components/ControlTable/columns';

import WithPermission from "../Common/WithPermission/WithPermission";
import ExportButton from '../Common/ExportButton/ExportButton';
import PageTitle from '../Common/PageTitle/PageTitle';
import Assetia from '../Common/Assetia/Assetia';

import { useAuth } from "../Context/AuthContext";

const Controls = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, userData } = useAuth();
  const isSuperuser = userData?.is_superuser === true;
  const hasAnyRole = userData?.roles?.length > 0;
  const canControlsRead = userData ? hasPermission("controls", "read") : false;
  const canControlsCreate = userData ? hasPermission("controls", "create") : false;

  const [api, contextHolder] = notification.useNotification();

  const [controls, setControls] = useState([]);
  const [controlsArea, setControlsArea] = useState([]);
  const [categories, setCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [types, setTypes] = useState([]);
  const [risks, setRisks] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingControl, setEditingControl] = useState(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [filteredData, setFilteredData] = useState([]); 
  const [searchText, setSearchText] = useState('');
  const [visibleData, setVisibleData] = useState([]);
  
  const [searchParams, setSearchParams] = useSearchParams();
  
  const dashboardTypeFilter = searchParams.get('type');
  const dashboardPriorityFilter = searchParams.get('priority');
  const areasFromUrl = searchParams.get('areas')?.split(',').filter(Boolean) || [];
  
  // Nuevos estados para filtros en cascada
  const [activeFilters, setActiveFilters] = useState({});
  
  // Detectar si los filtros vienen del dashboard
  const hasUrlFilters = dashboardTypeFilter || dashboardPriorityFilter || areasFromUrl.length > 0;

  // Sincronizar filtros del dashboard con activeFilters
  useEffect(() => {
    const initialFilters = {};
    
    if (dashboardTypeFilter) {
      initialFilters.type = [dashboardTypeFilter];
    }
    
    if (dashboardPriorityFilter) {
      initialFilters.priority = [dashboardPriorityFilter];
    }
    
    if (areasFromUrl.length > 0) {
      initialFilters.area = areasFromUrl;
    }
    
    // Solo setear si hay filtros del dashboard
    if (Object.keys(initialFilters).length > 0) {
      setActiveFilters(initialFilters);
    }
  }, [dashboardTypeFilter, dashboardPriorityFilter, areasFromUrl.join(',')]);

  const fetchControls = async () => {
    setLoading(true);
    try {
      const response = await getControls();
      const formattedControls = response.map(control => ({
        id: control.code,
        code: control.code, 
        title: control.title, 
        description: control.description,
        area: control.area ? { id: control.area.id, name: control.area.name } : null,
        category: control.category ? { id: control.category.id, name: control.category.name } : null,
        priority: control.priority ? { id: control.priority.id, name: control.priority.name } : null,
        framework: control.framework ? { id: control.framework.id, name: control.framework.name } : null,
        type: control.type ? { id: control.type.id, name: control.type.name } : null,
        risk: control.risk ? { id: control.risk.id, name: control.risk.name } : null
      }));
      
      setControls(formattedControls);
      setFilteredData(formattedControls);
      setVisibleData(formattedControls);
    } catch {
      api.error({ message: "Error al cargar los controles"});
    } finally {
      setLoading(false);
    }
  };

  const fetchAllOptions = async () => {
    try {
      const [areasData, categoriesData, prioritiesData, frameworksData, typesData, risksData] = await Promise.all([
        getControlsArea("controls"),
        getCategories(),
        getPriorities(),
        getFrameworks(),
        getTypes(),
        getRisks(),
      ]);
      
      setControlsArea(areasData);
      setCategories(categoriesData);
      setPriorities(prioritiesData);
      setFrameworks(frameworksData);
      setTypes(typesData);
      setRisks(risksData);
    } catch (error) {
      api.error({ message: "Error al cargar las opciones de configuración" });
    }
  };
  
  const handleSave = async (values) => {
    try {
      if (editingControl) {
        await updateControl(editingControl.code, {
          title: values.title,
          description: values.description,
          area_id: values.area,
          category_id: values.category,
          priority_id: values.priority,
          framework_id: values.framework,
          type_id: values.type,
          risk_id: values.risk
        });
        api.success({ message: "Control actualizado."});
      } else {
        await createControl({
          title: values.title, 
          description: values.description,
          area_id: values.area,
          category_id: values.category,
          priority_id: values.priority,
          framework_id: values.framework,
          type_id: values.type,
          risk_id: values.risk
        });
        api.success({ message: "Control agregado."});
      }
      setIsModalOpen(false);
      form.resetFields();
      setEditingControl(null);
      fetchControls();
    } catch (error) {
      api.error({ 
        message: "Error al guardar el control.",
        description: error?.response?.data?.title?.[0].includes("already exists") 
          ? "Ya existe un control con ese nombre."
          : "Ocurrió un error inesperado al guardar el control.",
        key: 'save-controls-error'
      });
    }
  };
  
  const handleDelete = async (code) => {
    try {
      await deleteControl(code); 
      api.success({ message: "Control eliminado."});
      fetchControls();
    } catch {
      api.error({ message: "Error al eliminar el control."});
    }
  };

  useEffect(() => {
    fetchControls();
  }, []);

  const showModal = async (control = null) => {
    await fetchAllOptions();

    if (control) {
      try {
        const controlData = await getControlsByCode(control.code);
        setEditingControl(controlData);
        form.setFieldsValue({
          id: controlData.code,
          title: controlData.title,
          description: controlData.description,
          area: controlData.area?.id,
          category: controlData.category?.id,
          priority: controlData.priority?.id,
          framework: controlData.framework?.id,
          type: controlData.type?.id,
          risk: controlData.risk?.id
        });
      } catch (error) {
        api.error({ message: "Error al cargar el control para edición." });
        return;
      }
    } else {
      setEditingControl(null);
      form.resetFields();
    }

    setIsModalOpen(true);
  };

  const controlTableFilter = () => {
    // Función helper para aplicar filtros
    const applyFilter = (data, filterKey, filterValues) => {
      if (!filterValues || filterValues.length === 0) return data;
      
      const filterMap = {
        'type': (control) => control.type?.name || '',
        'priority': (control) => control.priority?.name || '',
        'area': (control) => control.area?.name || '',
        'category': (control) => control.category?.name || '',
        'framework': (control) => control.framework?.name || '',
        'risk': (control) => control.risk?.name || '',
      };
      
      const getter = filterMap[filterKey];
      if (!getter) return data;
      
      return data.filter(control => {
        const value = getter(control);
        return filterValues.includes(value);
      });
    };

    // Obtener datos filtrados excluyendo una columna
    const getFilteredDataForColumn = (excludeColumn) => {
      let data = [...controls];
      
      Object.entries(activeFilters).forEach(([key, values]) => {
        if (key !== excludeColumn && values && values.length > 0) {
          data = applyFilter(data, key, values);
        }
      });
      
      return data;
    };

    // Generar opciones dinámicas basadas en filtros activos
    const typesData = getFilteredDataForColumn('type');
    const controlTypes = Array.from(
      new Map(typesData.map(c => [c.type?.id, c.type]).filter(([id, type]) => id && type)).values()
    );
  
    const prioritiesData = getFilteredDataForColumn('priority');
    const controlPriorities = Array.from(
      new Map(prioritiesData.map(c => [c.priority?.id, c.priority]).filter(([id, p]) => id && p)).values()
    );

    const categoriesData = getFilteredDataForColumn('category');
    const controlCategories = Array.from(
      new Map(categoriesData.map(c => [c.category?.id, c.category]).filter(([id, cat]) => id && cat)).values()
    );
  
    const areasData = getFilteredDataForColumn('area');
    const controlAreas = Array.from(
      new Map(areasData.map(c => [c.area?.id, c.area]).filter(([id, area]) => id && area)).values()
    );
  
    const frameworksData = getFilteredDataForColumn('framework');
    const controlFrameworks = Array.from(
      new Map(frameworksData.map(c => [c.framework?.id, c.framework]).filter(([id, fw]) => id && fw)).values()
    );
    
    const risksData = getFilteredDataForColumn('risk');
    const controlRisks = Array.from(
      new Map(risksData.map(c => [c.risk?.id, c.risk]).filter(([id, risk]) => id && risk)).values()
    );

    return {
      types: controlTypes,
      priorities: controlPriorities,
      categories: controlCategories,
      areas: controlAreas,
      frameworks: controlFrameworks,
      risks: controlRisks,
      dashboardTypeFilter: dashboardTypeFilter || null,
      dashboardPriorityFilter: dashboardPriorityFilter || null,
      filteredTypeValue: dashboardTypeFilter ? [dashboardTypeFilter] : [],
      filteredPriorityValue: dashboardPriorityFilter ? [dashboardPriorityFilter] : [],
      dashboardAreasFilter: areasFromUrl,
    };
  };

  // Función para aplicar filtros a los datos (para el filtro principal)
  const applyFiltersToData = (filters) => {
    let filtered = [...controls];
    
    Object.entries(filters).forEach(([key, values]) => {
      if (!values || values.length === 0) return;
      
      switch (key) {
        case 'type':
          filtered = filtered.filter(control => 
            values.includes(control.type?.name)
          );
          break;
        case 'priority':
          filtered = filtered.filter(control => 
            values.includes(control.priority?.name)
          );
          break;
        case 'area':
          filtered = filtered.filter(control => 
            values.includes(control.area?.name)
          );
          break;
        case 'category':
          filtered = filtered.filter(control => 
            values.includes(control.category?.name)
          );
          break;
        case 'framework':
          filtered = filtered.filter(control => 
            values.includes(control.framework?.name)
          );
          break;
        case 'risk':
          filtered = filtered.filter(control => 
            values.includes(control.risk?.name)
          );
          break;
        default:
          break;
      }
    });
    
    return filtered;
  };

  // Actualizar filteredData cuando cambien los filtros o los controles
  useEffect(() => {
    const filtered = applyFiltersToData(activeFilters);
    setFilteredData(filtered);
  }, [controls, activeFilters]);

  const handleTableChange = (pagination, filters, sorter, extra) => {
    // Guardar filtros activos
    setActiveFilters(filters);
    
    // Mantener la funcionalidad de visibleData
    setVisibleData(extra.currentDataSource);
    
    // ✅ Sincronizar con URL (manteniendo la funcionalidad existente)
    // Solo modificar URL si hay filtros del dashboard
    if (hasUrlFilters) {
      const newParams = new URLSearchParams();
      
      if (filters.type && filters.type.length > 0) {
        newParams.set('type', filters.type[0]);
      }
      
      if (filters.priority && filters.priority.length > 0) {
        newParams.set('priority', filters.priority[0]);
      }
      
      if (filters.area && filters.area.length > 0) {
        newParams.set('areas', filters.area.join(','));
      }
      
      // Preservar otros parámetros si existen
      const currentParams = new URLSearchParams(location.search);
      currentParams.forEach((value, key) => {
        if (!['type', 'priority', 'areas'].includes(key)) {
          newParams.set(key, value);
        }
      });
      
      navigate({
        pathname: '/controls',
        search: newParams.toString() ? `?${newParams.toString()}` : ''
      }, { replace: true });
    }
  };

  const permittedAreas = controlsArea.filter(area =>
    isSuperuser || hasPermission("controls", "create", area.id)
  );

  const inputRef = useRef(null);

  useEffect(() => {
    if (isModalOpen && !editingControl) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 200);
    }
  }, [isModalOpen, editingControl]);

  return (
    <WithPermission
      isSuperuser={isSuperuser}
      hasAnyRole={hasAnyRole}
      requiredPermission="read"
      currentPermission={canControlsRead}
      redirectTo="/home"
    >
      <>
        {contextHolder}
        <div>
          <div className="button-container">
            <Row justify="start">
              <Col span={23}>
                <PageTitle title="Inventario de Controles" icon="controls" />
              </Col>
            </Row>
            <Row justify="start" gutter={[0, 16]}>
              <Col span={16}>
                <Button
                  disabled={!isSuperuser && (!hasAnyRole || !canControlsCreate)}
                  className="add-button" 
                  onClick={() => showModal()}>
                  <PlusOutlined />
                  Agregar Control
                </Button>
              </Col>
              <Col span={8} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <ControlSearch
                  controlsData={controls}
                  searchText={searchText}
                  setSearchText={setSearchText}
                  setFilteredData={setFilteredData}
                />
              </Col>
            </Row>

            <Row justify="end" gutter={[8, 0]}>
              {
                isSuperuser &&
                <Col>
                  <Button 
                    type="primary"
                    className="view-deleted-button"
                    onClick={() => {
                      const params = new URLSearchParams();
                      if (dashboardTypeFilter) params.set('type', dashboardTypeFilter);
                      if (dashboardPriorityFilter) params.set('priority', dashboardPriorityFilter);
                      if (areasFromUrl.length) params.set('areas', areasFromUrl.join(','));
                      navigate(`/controls-deleted${params.toString() ? `?${params.toString()}` : ''}`);
                    }}>
                    <AppstoreFilled />
                    Ver Borrados
                  </Button>
                </Col>
              }
              <Col>
                <ExportButton
                  api={api}
                  contextHolder={contextHolder}
                  data={filteredData}
                  filename="Controles.csv"
                  transformData={(data) =>
                    data.map(item => ({
                      Nombre: item.title,
                      Descripción: item.description,
                      Riesgo: item.risk?.name || 'N/A',
                      Categoría: item.category?.name || 'N/A',
                      Tipo: item.type?.name || 'N/A',
                      Prioridad: item.priority?.name || 'N/A',
                      Responsable: item.area?.name || 'N/A',
                      Framework: item.framework?.name || 'N/A'
                    }))
                  }
                />
              </Col>
            </Row>
          </div>

          <Spin className="custom-spin" spinning={loading}>
            <Table
              className="custom-pagination"
              columns={Columns(hasPermission, showModal, handleDelete, controlTableFilter(), isSuperuser, hasAnyRole)} 
              scroll={{ y: 450 }}
              dataSource={filteredData} 
              rowKey="id"  
              key={controls.length} 
              pagination={{
                defaultPageSize: 30,
                showSizeChanger: true,
                pageSizeOptions: ['30', '50', '100'],
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`
              }}
              onChange={handleTableChange}
            />
          </Spin>

          <ControlsModal
            areas={permittedAreas}
            categories={categories}
            priorities={priorities}
            frameworks={frameworks}
            types={types}
            risks={risks}
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            form={form}
            handleSave={handleSave}
            editingControl={editingControl}
            inputRef={inputRef}
          />
        </div>
        <Row style={{ marginTop: 80 }}>
          <Assetia
            pageTitle="controles"
            pageDescription="cargar tus controles y gestionarlos de manera simple"
            view="controls"
          />
        </Row>
      </>
    </WithPermission>
  );
};

export default Controls;